// The v3 payment_plans API replaces the v1 scheduled_payments_rules one, but its contract differs:
// filters are flat query params rather than filter[...] pairs, amounts are numbers rather than
// strings, next_payment_time is next_payment_date, and the payment method is flat instead of
// nested. Callers keep speaking v1, so both directions are translated here.
//
// TODO: (VCITA2-15351 - rollout.payments.payables_linked_payment_plans cleanup): once the flag is
// released to all users, delete this util and speak v3 at the call sites instead. The views can
// then hold the numeric amount and format it with Intl, rather than the v1 decimal string
// toDecimalString reproduces here for compatibility.
// pov/src/modules/payments/utils/paymentPlanUtil.js carries the same conversion for the pov app -
// the two have no shared layer to live in, so fix and delete them together.

/**
 * Translates v1 index filters into v3 query params.
 *
 * filter[engagement_uid] is the only v1 filter translated, because it is the only one any caller
 * passes. v3 also accepts status, payment_method_type, payment_method_uid, client_uid and
 * payment_request_uid - add the mapping here before passing one, or it reaches v3 under its v1
 * name and is ignored without an error.
 *
 * The sort is pinned because v1 passed none and returned rows unordered - in practice oldest
 * first, which the matter list reverses into newest first - while v3 defaults to updated_at:desc.
 * A caller's own sort still wins.
 *
 * @param {{'filter[engagement_uid]'?: string, page?: number, per_page?: number, sort?: string}} params
 * @returns {{matter_uid?: string, page?: number, per_page?: number, sort: string}}
 */
export const toPaymentPlanParams = ({'filter[engagement_uid]': matterUid, ...params}) => ({
  sort: 'created_at:asc',
  ...params,
  ...(matterUid && {matter_uid: matterUid}),
});

/**
 * Flattens a v1 scheduled_payments_rule body into the v3 payment plan body.
 *
 * The amount guard is != null so a null is omitted rather than sent as Number(null) === 0 - same
 * reasoning as the read path, a missing amount should not be invented.
 *
 * @param {{name?: string, amount?: string|number, matter_uid?: string, send_receipt?: boolean,
 *          payment_method?: {type: string, uid: string}}} plan
 * @returns {{name?: string, amount?: number, matter_uid?: string, send_receipt?: boolean,
 *            payment_method_type?: string, payment_method_uid?: string}}
 */
export const toPaymentPlanBody = ({payment_method: paymentMethod, amount, ...plan}) => ({
  ...plan,
  ...(amount != null && {amount: Number(amount)}),
  ...(paymentMethod && {
    payment_method_type: paymentMethod.type,
    payment_method_uid: paymentMethod.uid,
  }),
});

/**
 * v1 served amounts as decimal strings ("50.0", "12.25") and the views interpolate them straight
 * into labels and multiply them, so a whole number has to keep its trailing ".0".
 *
 * @param {number} amount
 * @returns {string}
 */
const toDecimalString = (amount) => (Number.isInteger(amount) ? amount.toFixed(1) : String(amount));

/**
 * Translates a v3 payment plan back into the v1 scheduled_payments_rule shape the views consume.
 *
 * Only amount_paid defaults: the v3 entity marks amount required and amount_paid optional. A
 * missing amount means something upstream is broken, and "$0.0" would hide that behind a number a
 * user could act on.
 *
 * @param {{uid: string, amount: number, amount_paid?: number, next_payment_date: ?string,
 *          payment_method_type: string, payment_method_uid: string}} plan
 * @returns {{uid: string, amount: string, amount_paid: string, next_payment_time: ?string,
 *            payment_method: {type: string, uid: string}}}
 */
export const toScheduledPaymentsRule = ({
  amount,
  amount_paid: amountPaid,
  next_payment_date: nextPaymentDate,
  payment_method_type: paymentMethodType,
  payment_method_uid: paymentMethodUid,
  ...plan
}) => ({
  ...plan,
  amount: toDecimalString(amount),
  amount_paid: toDecimalString(amountPaid ?? 0),
  next_payment_time: nextPaymentDate,
  payment_method: {type: paymentMethodType, uid: paymentMethodUid},
});
